import React from 'react';
import type { ReactElement } from 'react';
import { spacingHelper, Checkbox } from '@construct-kit/core';
import styled, { css } from 'styled-components';
import type { FilterOptionType } from '../../../../shared/model/constants/filterTypes';
import { FilterWrapper } from './FilterWrapper';
import type { PropertyType } from '../../../../shared/model/tsTypes';

const ALL_PROPERTY_TYPES = 'all';

export type PropertyTypesFilterProps = {
  options: FilterOptionType[];
  values: Array<FilterOptionType['value']>;
  hideHeading?: boolean;
  showDivider?: boolean;
  onChange: (newValue: Array<PropertyType['id']>) => void;
};

const StyledCheckbox = styled(Checkbox)`
  flex: 0 50%;
  margin-top: ${spacingHelper('large')};
`;

const CheckboxContainer = styled.div`
  ${({ theme }) => css`
    ${theme.layoutBreakpoints.large} {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      column-gap: ${spacingHelper('medium')};
    }
  `}
`;

export const PropertyTypesFilter = ({
  options,
  values,
  hideHeading,
  showDivider,
  onChange,
}: PropertyTypesFilterProps): ReactElement => {
  const isSelected = (v: string) => values.includes(v);

  const calculateSelected = (selectedValue: PropertyType['id']) => {
    if (selectedValue === ALL_PROPERTY_TYPES) {
      return [];
    }
    if (isSelected(selectedValue)) {
      return values.filter((value) => value !== selectedValue);
    }
    return [...values, selectedValue];
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = event.target.value;
    const newSelectedArr = calculateSelected(selectedValue);

    onChange(newSelectedArr);
  };

  return (
    <FilterWrapper title="Property type" hideHeading={hideHeading} showDivider={showDivider}>
      <CheckboxContainer>
        <StyledCheckbox
          checked={values.length === 0}
          label="All types"
          value={ALL_PROPERTY_TYPES}
          onChange={handleCheckboxChange}
          sizeVariant="large"
        />
        {options.map(({ label, value }: FilterOptionType) => (
          <StyledCheckbox
            checked={isSelected(value)}
            label={label}
            value={value}
            key={value}
            onChange={handleCheckboxChange}
            sizeVariant="large"
          />
        ))}
      </CheckboxContainer>
    </FilterWrapper>
  );
};
